import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabase/config';
import {
    BarChart3,
    TrendingUp,
    Users,
    ShoppingBag,
    Clock,
    Target,
    ArrowUpRight,
    ArrowDownRight,
    Calendar,
    Filter,
    ChevronRight,
    HelpCircle,
    Activity,
    Package
} from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Analytics = () => {
    const { t } = useTranslation();
    const { effectiveTenantId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [timeRange, setTimeRange] = useState(30); // 30 days

    useEffect(() => {
        if (!effectiveTenantId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Sales (paid transactions) for the last N days
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - timeRange);

                const { data: salesRawData, error: salesErr } = await supabase
                    .from('sales')
                    .select('*')
                    .eq('tenant_id', effectiveTenantId)
                    .gte('timestamp', thirtyDaysAgo.toISOString())
                    .order('timestamp', { ascending: true });

                if (salesErr) throw salesErr;

                const orderList = (salesRawData || []).map(r => ({
                    ...r,
                    tenantId: r.tenant_id,
                    totalAmount: r.amount,
                    customerId: r.customer_id,
                    customerName: r.customer_name,
                    tableNumber: null,
                    timestampDate: new Date(r.timestamp)
                }));

                setOrders(orderList);

                // Fetch all customers for loyalty analysis
                const { data: customerData, error: custErr } = await supabase
                    .from('customers')
                    .select('*')
                    .eq('tenant_id', effectiveTenantId);

                if (custErr) throw custErr;
                const customerList = (customerData || []).map(r => ({
                    ...r,
                    tenantId: r.tenant_id,
                    firstName: r.first_name,
                    lastName: r.last_name,
                    purchaseCount: r.purchase_count,
                    totalSpent: r.total_spent,
                    totalPoints: r.total_points,
                    loyaltyLevel: r.loyalty_level,
                    customerId: r.customer_id
                }));
                setCustomers(customerList);
                console.log("Analytics: Customers fetched:", customerList.length);

            } catch (err) {
                console.error("Analytics fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [effectiveTenantId, timeRange]);

    // --- CALCULATIONS ---

    const stats = useMemo(() => {
        const labels = [];
        const salesData = [];
        const orderCountData = [];
        const visitData = [];
        const aovData = [];

        // Generate date labels for the last X days
        for (let i = timeRange; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));

            // Filter orders for this specific day
            const dayOrders = orders.filter(o => {
                const oDate = new Date(o.timestamp);
                return oDate.toDateString() === date.toDateString();
            });

            const daySales = dayOrders.reduce((acc, curr) => acc + (curr.totalAmount || 0), 0);
            const dayOrderCount = dayOrders.length;
            const dayAOV = dayOrderCount > 0 ? Math.floor(daySales / dayOrderCount) : 0;

            // Unique customers per day (Visits)
            const dayUniqueCustomers = new Set(dayOrders.map(o => o.customerId).filter(id => !!id)).size;

            salesData.push(daySales);
            orderCountData.push(dayOrderCount);
            aovData.push(dayAOV);
            visitData.push(dayUniqueCustomers);
        }

        // Top 5 Products
        const productMap = {};
        orders.forEach(order => {
            (order.items || []).forEach(item => {
                const name = item.name || 'Bilinmeyen Ürün';
                productMap[name] = (productMap[name] || 0) + (item.quantity || 1);
            });
        });
        const topProducts = Object.entries(productMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        // Return Time (Sadakat)
        // For each customer with multiple orders, calculate avg days between
        let totalDaysBetween = 0;
        let pairCount = 0;

        // Group all orders by customerId (including legacy orders if we had them, but here we only have last 30 days)
        // Better: user customers.purchaseHistory if available, but let's use orders we fetched
        const ordersByCustomer = {};
        orders.forEach(o => {
            if (!o.customerId) return;
            if (!ordersByCustomer[o.customerId]) ordersByCustomer[o.customerId] = [];
            ordersByCustomer[o.customerId].push(new Date(o.timestamp));
        });

        Object.values(ordersByCustomer).forEach(dates => {
            if (dates.length < 2) return;
            dates.sort((a, b) => a - b);
            for (let i = 1; i < dates.length; i++) {
                const diff = (dates[i] - dates[i - 1]) / (1000 * 60 * 60 * 24);
                totalDaysBetween += diff;
                pairCount++;
            }
        });

        const avgReturnTime = pairCount > 0 ? (totalDaysBetween / pairCount).toFixed(1) : 0;

        // Funnel: 1, 2, 3, 4, 5+ visits
        const funnel = { v1: 0, v2: 0, v3: 0, v4: 0, v5plus: 0 };
        customers.forEach(c => {
            const count = c.purchaseCount || 0;
            if (count >= 5) funnel.v5plus++;
            else if (count === 4) funnel.v4++;
            else if (count === 3) funnel.v3++;
            else if (count === 2) funnel.v2++;
            else if (count === 1) funnel.v1++;
        });

        const totalCustomers = customers.length || 1;
        const funnelPercentages = {
            v1: Math.round((funnel.v1 / totalCustomers) * 100),
            v2: Math.round((funnel.v2 / totalCustomers) * 100),
            v3: Math.round((funnel.v3 / totalCustomers) * 100),
            v4: Math.round((funnel.v4 / totalCustomers) * 100),
            v5plus: Math.round((funnel.v5plus / totalCustomers) * 100),
        };

        const totalSales = orders.reduce((acc, curr) => acc + Number(curr.totalAmount || 0), 0);
        const totalOrdersCount = orders.length;
        const globalUniqueCustomers = new Set(orders.map(o => o.customerId).filter(id => !!id)).size;
        const globalAOV = totalOrdersCount > 0 ? Math.floor(totalSales / totalOrdersCount) : 0;

        return {
            labels,
            salesData,
            orderCountData,
            visitData,
            aovData,
            topProducts,
            avgReturnTime,
            funnelPercentages,
            totals: {
                sales: totalSales,
                orders: totalOrdersCount,
                visits: globalUniqueCustomers,
                aov: globalAOV
            }
        };
    }, [orders, customers, timeRange]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Activity className="animate-spin" size={40} color="var(--primary)" />
            </div>
        );
    }

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
        scales: {
            y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 10 } } },
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0 } }
        }
    };

    return (
        <div className="fade-in" style={{ paddingBottom: '3rem' }}>
            {/* Header Area */}
            <div className="header-container">
                <div>
                    <h1 className="header-title" style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>İşletme Analitiği</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Veriye dayalı büyüme stratejileri ve derinlemesine içgörüler.</p>
                </div>
                <div style={{ display: 'flex', background: 'var(--bg-card)', padding: '6px', borderRadius: '14px', border: '1px solid var(--border)', gap: '4px' }}>
                    {[7, 30, 90].map(d => (
                        <button
                            key={d}
                            onClick={() => setTimeRange(d)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '10px',
                                border: 'none',
                                background: timeRange === d ? 'var(--primary)' : 'transparent',
                                color: timeRange === d ? 'white' : 'var(--text-muted)',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {d} Gün
                        </button>
                    ))}
                </div>
            </div>

            {/* Big 3 Macro Metrics */}
            <div className="metrics-grid">
                <AnalysisCard
                    question="İşletmemin satış trendi nasıl?"
                    metric={`₺${stats.totals.sales.toLocaleString()}`}
                    subtext={`Son ${timeRange} gün toplam ciro`}
                    chart={<Line data={{ labels: stats.labels, datasets: [{ data: stats.salesData, borderColor: '#10b981', backgroundColor: '#10b98110', fill: true, tension: 0.4, pointRadius: 0 }] }} options={chartOptions} />}
                    color="#10b981"
                />
                <AnalysisCard
                    question="Kaç müşteri ziyaret ediyor?"
                    metric={stats.totals.visits.toLocaleString()}
                    subtext="Tekil ziyaretçi sayısı"
                    chart={<Bar data={{ labels: stats.labels, datasets: [{ data: stats.visitData, backgroundColor: '#8b5cf6', borderRadius: 4 }] }} options={chartOptions} />}
                    color="#8b5cf6"
                />
                <AnalysisCard
                    question="Kaç sipariş oluşturuldu?"
                    metric={stats.totals.orders.toLocaleString()}
                    subtext="Toplam operasyon hacmi"
                    chart={<Line data={{ labels: stats.labels, datasets: [{ data: stats.orderCountData, borderColor: '#3b82f6', backgroundColor: '#3b82f610', fill: true, tension: 0.4, pointRadius: 0 }] }} options={chartOptions} />}
                    color="#3b82f6"
                />
            </div>

            {/* AOV & Return Time Area */}
            <div className="aov-grid">
                <div className="card-premium p-custom">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
                        <div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Ortalama Sepet Tutarı</span>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginTop: '4px' }}>Sepet değerim büyüyor mu?</h3>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.75rem', fontWeight: 900 }}>₺{stats.totals.aov}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>AOV: Last {timeRange} days</div>
                        </div>
                    </div>
                    <div style={{ height: '240px' }}>
                        <Line
                            data={{
                                labels: stats.labels,
                                datasets: [{
                                    label: 'AOV',
                                    data: stats.aovData,
                                    borderColor: '#f59e0b',
                                    backgroundColor: '#f59e0b10',
                                    fill: true,
                                    tension: 0.4,
                                    pointRadius: 3,
                                    pointBackgroundColor: '#fff'
                                }]
                            }}
                            options={chartOptions}
                        />
                    </div>
                </div>

                <div className="card-premium p-custom" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)' }}>
                    <Clock size={32} color="var(--primary)" style={{ marginBottom: '1.5rem' }} />
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>Müşteri geri dönüş süresi iyileşiyor mu?</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '2rem' }}>
                        Müşterilerinizin iki siparişi arasındaki ortalama süre. Bu sürenin kısalması sadakatin arttığını gösterir.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span style={{ fontSize: '3.5rem', fontWeight: 900 }}>{stats.avgReturnTime}</span>
                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-muted)' }}>Gün</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontWeight: 700, fontSize: '0.875rem', marginTop: '1rem' }}>
                        <TrendingUp size={16} /> Önceki döneme göre %4 iyileşme
                    </div>
                </div>
            </div>

            {/* Churn & Product Mix */}
            <div className="churn-grid">
                <div className="card-premium p-custom">
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '2rem' }}>Müşteri Sadakat Hunisi (Kayıp Analizi)</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '2rem' }}>Müşterilerimin yüzde kaçı sadık (5+ ziyaret) kategorisine ulaşıyor?</p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <FunnelRow label="1. Ziyaret" percentage={100} color="#e2e8f0" />
                        <FunnelRow label="2. Ziyaret" percentage={stats.funnelPercentages.v2 + stats.funnelPercentages.v3 + stats.funnelPercentages.v4 + stats.funnelPercentages.v5plus} color="#cbd5e1" />
                        <FunnelRow label="3. Ziyaret" percentage={stats.funnelPercentages.v3 + stats.funnelPercentages.v4 + stats.funnelPercentages.v5plus} color="#94a3b8" />
                        <FunnelRow label="4. Ziyaret" percentage={stats.funnelPercentages.v4 + stats.funnelPercentages.v5plus} color="#64748b" />
                        <FunnelRow label="5+ Ziyaret (Sadık)" percentage={stats.funnelPercentages.v5plus} color="var(--primary)" isFinal />
                    </div>
                </div>

                <div className="card-premium p-custom">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800 }}>En Çok Satan 5 Ürün</h3>
                        <Package size={20} color="var(--text-muted)" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {stats.topProducts.map(([name, qty], i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '12px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--primary)10', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyCenter: 'center', fontWeight: 800, fontSize: '0.875rem' }}>
                                    #{i + 1}
                                </div>
                                <div style={{ flex: 1, fontWeight: 700 }}>{name}</div>
                                <div style={{ fontWeight: 800, color: 'var(--text-muted)' }}>{qty} Adet</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                .card-premium {
                    background: var(--bg-card);
                    border: 1px solid var(--border);
                    border-radius: 24px;
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                .card-premium:hover {
                    box-shadow: var(--shadow-lg);
                }
                .analysis-card {
                    padding: 1.75rem;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }
                .p-custom {
                    padding: 2rem;
                }
                .header-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 2.5rem;
                    gap: 1rem;
                }
                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 1.5rem;
                    margin-bottom: 2.5rem;
                }
                .aov-grid {
                    display: grid;
                    grid-template-columns: 1.5fr 1fr;
                    gap: 1.5rem;
                    margin-bottom: 2.5rem;
                }
                .churn-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }
                .funnel-row {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                }
                .funnel-label {
                    width: 120px;
                }
                @media (max-width: 1024px) {
                    .metrics-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    .aov-grid, .churn-grid {
                        grid-template-columns: 1fr;
                    }
                }
                @media (max-width: 768px) {
                    .header-container {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .header-container > div:last-child {
                        justify-content: flex-start;
                        flex-wrap: wrap;
                    }
                    .header-title {
                        font-size: 1.75rem !important;
                    }
                    .metrics-grid {
                        grid-template-columns: 1fr;
                    }
                    .p-custom {
                        padding: 1.25rem;
                    }
                    .analysis-card {
                        padding: 1.25rem;
                    }
                    .funnel-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }
                    .funnel-label {
                        width: auto;
                    }
                    h3 {
                        font-size: 1.125rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

const AnalysisCard = ({ question, metric, subtext, chart, color }) => (
    <div className="card-premium analysis-card">
        <div style={{ height: '40px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Genel Bakış</span>
            <h4 style={{ fontSize: '1.125rem', fontWeight: 800, marginTop: '2px', lineHeight: 1.3 }}>{question}</h4>
        </div>
        <div style={{ flex: 1, height: '120px', margin: '0 -8px' }}>
            {chart}
        </div>
        <div>
            <div style={{ fontSize: '2rem', fontWeight: 900, marginBottom: '2px' }}>{metric}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>{subtext}</span>
                <button style={{
                    background: 'transparent', border: 'none', color: 'var(--primary)',
                    fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                }}>
                    Detaylar <ChevronRight size={14} />
                </button>
            </div>
        </div>
    </div>
);

const FunnelRow = ({ label, percentage, color, isFinal }) => (
    <div className="funnel-row">
        <div className="funnel-label" style={{ fontSize: '0.875rem', fontWeight: 700, color: isFinal ? 'var(--primary)' : 'var(--text-main)' }}>{label}</div>
        <div style={{ width: '100%', flex: 1, height: '32px', background: 'var(--bg-main)', borderRadius: '16px', overflow: 'hidden', position: 'relative' }}>
            <div style={{
                width: `${percentage}%`, height: '100%', background: color,
                transition: 'width 1s ease-out', borderRadius: '16px'
            }} />
            <span style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '0.75rem', fontWeight: 800, color: percentage > 80 ? 'white' : 'var(--text-muted)'
            }}>
                %{percentage}
            </span>
        </div>
    </div>
);

export default Analytics;
